import { IconName, StickerVariant } from '@bytel/trilogy-react-ts';

import { PaymentTicketResponse } from './api/order';
import { CustomerAddress, DeliveryModeMapped } from './delivery';
import { ContentEditorImageType, DiscountType, ObligationType } from './fai';
import { CheckoutFundingType, PaymentMethodType } from './order';
import { PRICE_TYPE } from './price';

export enum JOURNEYS {
    BYOU = 'byou',
    FAI = 'fai',
}

export enum OPTION_GENCODES {
    PARENTAL_CONTROL_BASIC = '11148',
    PARENTAL_CONTROL_PREMIUM = '13667',
    FIVE_G = '13512',
    BYOU_MAX = '13985',
}

export enum FundingEnumType {
    EDP = 'EDP',
    CASH = 'COMPTANT',
    YOUNITED = 'YOUNITED',
    YOUNITED_X3 = 'YOUNITED_X3',
    YOUNITED_X12 = 'YOUNITED_X12',
    YOUNITED_X36 = 'YOUNITED_X36',
    YOUNITED_X24 = 'YOUNITED_X24',
    COFIDIS = 'COFIDIS',
    COFIDIS_X12 = 'COFIDIS_X12',
    COFIDIS_X24 = 'COFIDIS_X24',
    COFIDIS_X36 = 'COFIDIS_X36',
}

export enum PlanRange {
    DEFAULT = '',
    B_YOU = 'b_you',
    WITH_MOBILE = 'forfait_avec_mobile',
    WITHOUT_MOBILE = 'forfait_sans_mobile',
    BBOX_ULTYM = 'large',
    BBOX_MUST = 'medium',
    BBOX_FIT = 'small',
}

export enum ProductTypeId {
    DEFAULT = 'default',
    PREMIUM = 'plan_premium',
    PREMIUM_SELECTION = 'plan_premium_selection',
    PREMIUM_PRO = 'plan_pro_premium',
    PREMIUM_RENEWAL = 'plan_premium_renew',
    PREMIUM_ENTRY = 'plan_premium_entry',
    PREMIUM_MULTILIGNE = 'plan_premium_multiligne',
    SIMO = 'plan_simo',
    SIMO_SELECTION = 'plan_simo_selection',
    SIMO_ENTRY = 'plan_simo_entry',
    SIMO_MULTILIGNE = 'plan_simo_multiligne',
    SIMO_PRO = 'plan_pro_simo',
    SOWO = 'plan_sowo',
    SOWO_RENEWAL = 'plan_sowo_renew',
    SOWO_SPECIALS = 'plan_sowo_specials',
    IDEO = 'plan_ideo',
    IDEO_PRO = 'plan_ideo_pro',
    FAIM = 'faim',
    PREMIUM_PRO_SELECTION = 'plan_pro_premium_selection',
    SIMO_PRO_SELECTION = 'plan_pro_simo_selection',
    FAI = 'fai',
    OPTION = 'option_salable',
    FAI_FMD = 'fai_fmd',
    PHONE_SIMPLE = 'phone_simple',
    PHONE = 'phone',
    ACCESSORY = 'accessory',
    ACCESSORY_CONFIGURABLE = 'accessory_configurable',
    BOX = 'box',
    FAIM_UNLIMITED = 'faim_unlimited',
    FAI_SERVICE = 'fai_service',
    SIM = 'sim',
}

export enum LockedPlanValue {
    NONE = '0',
    ONE = '1',
    TWO = '2',
}

export enum Connectivity {
    FIVE_G = '5g',
    FOUR_G = '4g',
    FOUR_G_PLUS = '4g+',
}

export type Product = {
    typeId: ProductTypeId;
    gencode: string;
    url?: string;
    // image: string;
    name: string;
    price: number;
    prices: PriceType;
    promoLabel?: string;
    discounts: DiscountType[];
    inStock: boolean;
    type?: PlanType;
    products: Product[];
    categories: string[];
    neededElements?: ProductNeedElement[];
};

export type RepeaterProduct = Product & {
    longDescription?: string;
    shortDescription?: string;
    textLink?: string;
    imgRepeater?: ContentEditorImageType;
};

export type ProductNeedElement = {
    alternatives: {
        typeCatalogue: string;
        commercialComponentCategories: string[];
        identifier: string;
        label: string;
    }[];
};

export type FundingType = {
    type: FundingEnumType;
    favorite: boolean;
    label: string;
    initialDeposit: number;
    monthlyAmount: number;
    APR: number;
    interestAmount: number;
    totalFinancingCost: number;
    amountToFinance: number;
    installmentCount?: number;
    fixedInterestRate?: number;
    initialContributionWithOdr?: number;
};

export type PriceType = {
    initial: number;
    subsidized?: number;
    forever: number;
    final: number;
    pricesWithBboxIncentive?: {
        pureFibreMax?: number;
        pureFibreStandard?: number;
        bboxPlusTv?: number;
    };
    duration?: number;
    promoLabel?: string;
    type?: PRICE_TYPE;
};

export type OptionType = {
    gencode: string;
    name: string;
    price: number;
    image: string;
    pennantText: string;
    promoLabel: string;
    backgroundImage?: {
        default: string;
        portrait: string;
    };
};

export type PlanType = Product & {
    commercialRange?: PlanRange;
    dataEnvelope: string;
    dataSize: number;
    fromAbroad?: string;
    gencode: string;
    price: number;
    obligation?: ObligationType;
    obligationLabel?: string;
    connectivity?: Connectivity;
    smsMms?: string;
    technology?: string;
    urlKey?: string;
    detailsUrl?: string;
    popInCms?: string;
    isOwned?: boolean;
    detailsDescription?: string;
    planFlag?: string; // vérifier si toujours existant
    locked?: LockedPlanValue;
    maxConnections?: string;
    roamingEnvelope: string;
    entityId?: string;
    position: number;
    specialPriceRenewal?: {
        discountAmount?: number;
        finalTTCAmount?: number;
    };
    rentalFees?: number;
    subtitle?: string;
    fanion?: string;
    options: OptionType[];
    newprice: number; // @todo remove

    // from content editor
    bannerText?: string | null;
    isHighlighted?: boolean;
    networkText?: string;

    // product type custom
    option5g?: LightOptionType;
    optionByouMax?: LightOptionType;
    radioFlag?: string;
    radioFlagColor?: StickerVariant | 'BYOU';
    addedPepites?: {
        icon?: IconName;
        pepite?: string;
    }[];
    textLinkPopin?: string;
    roamingPopinTitle?: string;
    roamingDestinations?: string;
    planPicto?: ContentEditorImageType[];
    stickerPosition?: number;
};

export type LightOptionType = {
    gencode?: string;
    price?: number;
    name?: string;
};

export type BboxType = {
    price: number;
    isSelected: boolean;
};

type Portablilty =
    | {
          keepNumber: true;
          codeRio: string;
          phoneNumber: string;
      }
    | { keepNumber: false; codeRio?: never; phoneNumber?: never };

export type CartType = {
    id?: number;
    gencode: string;
    product?: PlanType;
    total: number;
    itemsCount: number;
    options: OptionType[];
    deliveryMode?: DeliveryModeMapped | null;
    sim?: SimType;
    orderId?: string;
    paymentTicket?: PaymentTicketResponse['retourPSP'];
    fundingMode?: PaymentMethodType;
    cartId?: number;
    quoteId?: number;
    fundingMethods?: CheckoutFundingType[];
    portability?: Portablilty;
    fingerprint?: string;
    address?: CustomerAddress;
    mobileFees?: Product;
    tempId: string;
    isMinor?: boolean;
    products?: Product[];
    expiresTimestamp: number;
};

export type SimType = ProductType & {
    preferred: boolean;
    esim: boolean;
};

export type ProductType = {
    gencode: string;
    price: number;
    priceType: PRICE_TYPE;
    typeId: ProductTypeId;
};
